import { useEffect, useState } from "react";
import axios from "axios";
import AuthLayout from "@container/layout/auth";
import { Card, CardContent } from "@component/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import ExecutiveSummaryCard from "@component/executive-summar-card";
import BillingHiringActivity from "@container/billing-hiring-activity";
import CompanyFilter from "./components/company-filter";
import JobsTable from "./components/jobs-table";
import { getAdminHiringMetrics } from "@service/admin";
import {
    AdminHiringDateRange,
    AdminHiringMetricsSummary,
} from "@type/admin";
import { catchErrorLog } from "@util/sentry";
import {
    BarChart3Icon,
    BriefcaseIcon,
    UsersIcon,
    ClockIcon,
    CheckCircle2Icon,
    TrendingDownIcon,
    AlertCircleIcon,
    Building2Icon,
    CalendarIcon,
    MailIcon,
} from "lucide-react";

// YYYY-MM-DD in local time (the value shape a native <input type="date"> uses).
function toYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

// Default window: the last 7 days (today inclusive), matching the old "7 days" preset.
function defaultRange(): AdminHiringDateRange {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { start_date: toYmd(start), end_date: toYmd(end) };
}

function formatPercentValue(value: number | undefined): string {
    if (value === undefined || !Number.isFinite(value)) return "0";
    return value.toFixed(0);
}

function formatCount(value: number | undefined): string {
    if (value === undefined || !Number.isFinite(value)) return "0";
    return value.toFixed(0);
}

function computeTrend(
    current: number | undefined,
    previous: number | undefined
): { trend?: "up" | "down"; trendValue?: string } {
    if (current === undefined || previous === undefined || previous === 0) return {};
    const ratio = (current - previous) / previous;
    if (!Number.isFinite(ratio) || ratio === 0) return {};
    // Hide the trend chip entirely on a full -100% drop (current went to 0
    // from a non-zero previous), since the "↓ 100%" pill is noisy and the
    // 0 value already conveys the drop.
    if (ratio <= -1) return {};
    return {
        trend: ratio > 0 ? "up" : "down",
        trendValue: `${(Math.abs(ratio) * 100).toFixed(0)}%`,
    };
}

// A single labelled native date input, styled to match the agent-calls date
// filters. `min`/`max` clamp the pickable range so From can't exceed To.
function DateRangeInput({
    label,
    value,
    onChange,
    min,
    max,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    min?: string;
    max?: string;
}) {
    return (
        <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
                {label}
            </p>
            <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                    type="date"
                    value={value}
                    min={min}
                    max={max}
                    onChange={(e) => onChange(e.target.value)}
                    className="pl-9 pr-3 h-9 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent dark:bg-gray-900 dark:border-gray-700 dark:text-white w-44"
                />
            </div>
        </div>
    );
}

export default function AdminHiringActivityPage() {
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
    const [range, setRange] = useState<AdminHiringDateRange>(defaultRange);
    const [metrics, setMetrics] = useState<AdminHiringMetricsSummary | null>(null);
    const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(true);
    const [metricsError, setMetricsError] = useState<string>("");

    const isRangeValid =
        !!range.start_date && !!range.end_date && range.start_date <= range.end_date;

    useEffect(() => {
        if (!selectedCompanyId) {
            setMetrics(null);
            setMetricsError("");
            setIsLoadingMetrics(false);
            return;
        }
        if (!isRangeValid) {
            setMetrics(null);
            setMetricsError("Start date must be on or before the end date.");
            setIsLoadingMetrics(false);
            return;
        }
        let cancelled = false;
        setIsLoadingMetrics(true);
        setMetricsError("");
        getAdminHiringMetrics({ company_id: selectedCompanyId, range })
            .then((resp) => {
                if (cancelled) return;
                setMetrics(resp.data?.data ?? null);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Hiring Activity: failed to fetch metrics: ${err}`);
                setMetrics(null);
                setMetricsError(
                    axios.isAxiosError(err)
                        ? (err.response?.data as { message?: string } | undefined)?.message
                            || "Failed to load hiring activity metrics."
                        : "Failed to load hiring activity metrics."
                );
            })
            .finally(() => {
                if (!cancelled) setIsLoadingMetrics(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedCompanyId, range, isRangeValid]);

    const prev = metrics?.prev_period ?? null;
    const current = metrics;

    return (
        <AuthLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                        <BarChart3Icon className="w-5 h-5" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Hiring Activity</h1>
                        <p className="text-sm text-neutral-500">
                            Cross-company hiring oversight. Filter by company or view aggregate data.
                        </p>
                    </div>
                </div>

                {/* Filter row */}
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <CompanyFilter value={selectedCompanyId} onChange={setSelectedCompanyId} />
                    {/* Date range — replaces the fixed 7/30/90/all presets. */}
                    <div className="flex items-end gap-2">
                        <DateRangeInput
                            label="From"
                            value={range.start_date}
                            max={range.end_date || undefined}
                            onChange={(v) => setRange((r) => ({ ...r, start_date: v }))}
                        />
                        <DateRangeInput
                            label="To"
                            value={range.end_date}
                            min={range.start_date || undefined}
                            onChange={(v) => setRange((r) => ({ ...r, end_date: v }))}
                        />
                    </div>
                </div>

                {!selectedCompanyId ? (
                    <Card>
                        <CardContent className="p-12 text-center">
                            <Building2Icon className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-gray-900 mb-1">
                                Select a company to view hiring activity.
                            </p>
                            <p className="text-xs text-neutral-500 max-w-md mx-auto">
                                Pick a company from the filter above to see KPIs, per-job breakdown, and activity tracking.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {metricsError && (
                            <Card className="border-red-200 bg-red-50">
                                <CardContent className="p-4 flex items-start gap-3">
                                    <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-red-700">{metricsError}</div>
                                </CardContent>
                            </Card>
                        )}

                        <Tabs defaultValue="overview" className="w-full">
                            <TabsList className="flex w-auto bg-transparent h-auto p-0 border-b border-gray-200 justify-start rounded-none">
                                <TabsTrigger
                                    value="overview"
                                    className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                                >
                                    <BarChart3Icon className="h-4 w-4" />
                                    Overview
                                </TabsTrigger>
                                <TabsTrigger
                                    value="jobs"
                                    className="text-sm flex items-center gap-2 bg-transparent border-0 rounded-none pb-3 px-4 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 hover:text-blue-600 transition-colors"
                                >
                                    <BriefcaseIcon className="h-4 w-4" />
                                    Per-Job Breakdown
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="overview" className="mt-5 space-y-5">
                                {/* KPI grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <ExecutiveSummaryCard
                                        label="Active Job Postings"
                                        value={formatCount(current?.total_active_job_posting)}
                                        icon={BriefcaseIcon}
                                        tooltip="Jobs currently open for the selected company."
                                        {...computeTrend(
                                            current?.total_active_job_posting,
                                            prev?.total_active_job_posting
                                        )}
                                    />
                                    <ExecutiveSummaryCard
                                        label="New Applications"
                                        value={formatCount(current?.total_new_applications)}
                                        icon={UsersIcon}
                                        tooltip="Applications submitted within the selected date range."
                                        {...computeTrend(
                                            current?.total_new_applications,
                                            prev?.total_new_applications
                                        )}
                                    />
                                    <ExecutiveSummaryCard
                                        label="Pending Review"
                                        value={formatCount(current?.total_pending_review)}
                                        icon={ClockIcon}
                                        tooltip="Candidates currently awaiting recruiter review."
                                        {...computeTrend(
                                            current?.total_pending_review,
                                            prev?.total_pending_review
                                        )}
                                    />
                                    <ExecutiveSummaryCard
                                        label="AI Screening Invited"
                                        value={formatCount(current?.total_assessment_invited)}
                                        icon={MailIcon}
                                        tooltip="Candidates invited to take a screening within the selected date range."
                                        {...computeTrend(
                                            current?.total_assessment_invited,
                                            prev?.total_assessment_invited
                                        )}
                                    />
                                    {(() => {
                                        const isNa =
                                            (current?.total_assessment_invited ?? 0) === 0 &&
                                            (current?.total_assessment_completion_rate ?? 0) === 0;
                                        return (
                                            <ExecutiveSummaryCard
                                                label="AI Screening Completion Rate"
                                                value={isNa ? "N/A" : formatPercentValue(current?.total_assessment_completion_rate)}
                                                icon={CheckCircle2Icon}
                                                suffix={isNa ? "" : "%"}
                                                tooltip="Percentage of invited candidates who completed the AI screening."
                                                {...(isNa ? {} : computeTrend(
                                                    current?.total_assessment_completion_rate,
                                                    prev?.total_assessment_completion_rate
                                                ))}
                                            />
                                        );
                                    })()}
                                    <ExecutiveSummaryCard
                                        label="Drop-off Rate"
                                        value={formatPercentValue(current?.total_drop_off_rate)}
                                        icon={TrendingDownIcon}
                                        suffix="%"
                                        tooltip="Percentage of candidates who started but did not complete the interview."
                                        {...computeTrend(
                                            current?.total_drop_off_rate,
                                            prev?.total_drop_off_rate
                                        )}
                                    />
                                </div>

                                {/* Activity tracking */}
                                <BillingHiringActivity
                                    scope="admin"
                                    companyId={selectedCompanyId}
                                    dateRange={isRangeValid ? range : undefined}
                                />
                            </TabsContent>

                            <TabsContent value="jobs" className="mt-5">
                                <JobsTable companyId={selectedCompanyId} range={range} />
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </div>
        </AuthLayout>
    );
}
